import { PageSection } from '@src/components';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { Badge, Button } from 'antd';
import clsx from 'clsx';
import { ArrangeVertical, ArrowDown, ArrowUp } from 'iconsax-react';
import { useEffect, useState } from 'react';

export interface ISortItem {
  id: string;
  label: string;
}

interface SortProps {
  sortList?: ISortItem[];
  prevFilters?: Record<string, boolean | 'asc' | 'desc'>;
  defaultSort?: Record<string, 'asc' | 'desc'>;
  selectedSort?: {
    type: string;
    direction: 'asc' | 'desc' | null;
  };
  onConfirm: (selected: Record<string, 'asc' | 'desc'>) => void;
  onCancel?: () => void;
}

export function Sort({
  sortList = [
    { id: 'alphabetical', label: 'Alphabetical' },
    { id: 'popular', label: 'Popular' },
  ],
  prevFilters,
  defaultSort,
  selectedSort: externalSelectedSort,
  onConfirm,
  onCancel,
}: SortProps) {
  const [open, setIsOpen] = useState(false);
  const [internalSelectedSort, setInternalSelectedSort] = useState<
    Record<string, 'asc' | 'desc' | null>
  >({});

  useEffect(() => {
    if (defaultSort) {
      setInternalSelectedSort(defaultSort);
    } else if (
      externalSelectedSort &&
      externalSelectedSort.type &&
      externalSelectedSort.direction
    ) {
      setInternalSelectedSort({
        [externalSelectedSort.type]: externalSelectedSort.direction,
      });
    } else if (prevFilters) {
      const firstSelected = Object.entries(prevFilters).find(
        ([, dir]) => !!dir
      );
      if (firstSelected) {
        setInternalSelectedSort({
          [firstSelected[0]]: firstSelected[1] === true ? 'asc' : 'desc',
        });
      }
    }
  }, [defaultSort, prevFilters, externalSelectedSort]);

  const toggleSort = (id: string) => {
    setInternalSelectedSort((prev) => {
      const current = prev[id] ?? null;
      const next = current === null ? 'asc' : current === 'asc' ? 'desc' : null;
      return { [id]: next };
    });
  };

  const handleCancel = () => {
    if (defaultSort) {
      setInternalSelectedSort(defaultSort);
    } else if (prevFilters) {
      const firstSelected = Object.entries(prevFilters).find(
        ([, dir]) => !!dir
      );
      if (firstSelected) {
        setInternalSelectedSort({
          [firstSelected[0]]: firstSelected[1] === true ? 'asc' : 'desc',
        });
      } else {
        setInternalSelectedSort({});
      }
    } else {
      setInternalSelectedSort({});
    }

    onCancel?.();
    setIsOpen(false);
  };

  const handleApply = () => {
    const sorted = Object.entries(internalSelectedSort).reduce(
      (acc, [id, dir]) => {
        if (dir === 'asc' || dir === 'desc') {
          acc[id] = dir;
        }
        return acc;
      },
      {} as Record<string, 'asc' | 'desc'>
    );

    onConfirm(sorted);
    setIsOpen(false);
  };

  const handleDiscard = () => {
    setInternalSelectedSort({});
    onConfirm({});
    setIsOpen(false);
  };

  // Calculate active sort count based on external applied sort, not internal selections
  const activeSortCount =
    externalSelectedSort && externalSelectedSort.direction ? 1 : 0;

  return (
    <div className='relative'>
      <Badge count={activeSortCount} offset={[-2, 2]}>
        <Button
          variant='text'
          icon={
            <ArrangeVertical
              variant='Linear'
              size='24'
              className='!text-wl-on-background'
            />
          }
          onClick={() => setIsOpen(true)}
          className='border-none shadow-none bg-transparent hover:!bg-transparent !py-0 !px-0'
        ></Button>
      </Badge>
      <ResponsiveModal
        isOpen={open}
        title='Sort'
        onClose={handleCancel}
        headerIcon={<ArrangeVertical size='16' />}
        content={
          sortList.length > 0 && (
            <div className='flex flex-col space-y-2 md:space-y-8 mb-3'>
              <PageSection className='flex flex-col gap-4 bg-wl-background py-4 px-6'>
                {sortList.map((item: ISortItem) => (
                  <Button
                    key={item.id}
                    size='large'
                    variant='filled'
                    onClick={() => toggleSort(item.id)}
                    className={clsx(
                      'flex items-center justify-between !text-wl-on-background',
                      '!bg-wl-surface !border-wl-border',
                      internalSelectedSort[item.id] && '!border-wl-primary'
                    )}
                  >
                    <span>{item.label}</span>
                    <span>
                      {internalSelectedSort[item.id] === 'asc' && (
                        <ArrowUp size='16' className='!text-wl-primary' />
                      )}
                      {internalSelectedSort[item.id] === 'desc' && (
                        <ArrowDown size='16' className='!text-wl-primary' />
                      )}
                    </span>
                  </Button>
                ))}
              </PageSection>

              <div className='flex w-full gap-4'>
                <Button className='btn-secondary w-1/2' onClick={handleDiscard}>
                  Discard
                </Button>
                <Button className='btn-primary w-1/2' onClick={handleApply}>
                  Apply
                </Button>
              </div>
            </div>
          )
        }
      />
    </div>
  );
}
